/**
 * Bearing display reference — whether bearings across the app read in degrees
 * true (°T) or magnetic (°M), and the magnetic variation used to convert.
 *
 * The app computes and stores every bearing in TRUE; this is purely a display
 * choice, applied at format time in nav/format.ts. We keep a tiny store here for
 * the UI, and mirror it into the pure formatter via setBearingDisplay (the same
 * pattern the haptics preference uses), so the many non-React formatters convert
 * without threading the setting through every call.
 *
 * Variation is held signed and East-positive: True = Magnetic + Variation, so
 * Magnetic = True − Variation. West variation is therefore a negative number.
 * There are two ways to get it:
 *
 *   - Manual: the menu enters it the way it reads off a chart's compass rose — a
 *     magnitude in degrees plus an East/West choice — which is how it's taught on
 *     RYA courses and how a paper-chart plotter works.
 *   - Auto: the World Magnetic Model works it out from the boat's position and
 *     the date (see AutoVariationRunner + nav/wmm.ts), so there's nothing to type.
 *
 * The value actually fed to the formatter is the "effective" one: the auto figure
 * when Auto is on and we have a computed value, otherwise the manual value. With
 * no position fix the auto figure is null, so we fall back to manual rather than
 * guessing.
 */
import { create } from 'zustand';
import { setBearingDisplay, type BearingMode } from '@/nav/format';

interface Persisted {
  mode: BearingMode;
  /** Signed manual variation, East-positive (West is negative). */
  variationDeg: number;
  /** Which hemisphere the dropdown last showed, so 0° keeps its E/W choice. */
  variationEast: boolean;
  /** Whether variation follows the World Magnetic Model at the boat's position. */
  auto: boolean;
}

const KEY = 'acp.bearingDisplay.v1';
// Default variation West (false): around the UK training area it sits a touch
// West, so an unconfigured plotter reads the right way rather than the wrong one.
// Auto defaults ON. The World Magnetic Model is already on board and the boat's
// position is already known, so there is nothing to gain from making somebody type
// a variation in before °M means anything, and plenty to lose: with Auto off and
// nothing typed the variation is zero, and every bearing then reads as a TRUE
// figure wearing a °M suffix. That is a wrong bearing presented as a right one,
// which is the one failure this store must not have.
const DEFAULTS: Persisted = { mode: 'true', variationDeg: 0, variationEast: false, auto: true };

function load(): Persisted {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(KEY) : null;
    if (!raw) return { ...DEFAULTS };
    const d = JSON.parse(raw) as Partial<Persisted>;
    const variationDeg = Number.isFinite(d.variationDeg) ? (d.variationDeg as number) : 0;
    // Older saves had no hemisphere flag; derive it from the sign so an existing
    // East value stays East. A stored 0 falls back to the West default.
    const variationEast =
      typeof d.variationEast === 'boolean' ? d.variationEast : variationDeg > 0;
    const mode: BearingMode = d.mode === 'magnetic' ? 'magnetic' : 'true';
    // Missing key means a save written before Auto existed, so take the default
    // (on) rather than reading the absence as a deliberate "off".
    let auto = d.auto !== false;
    // Repair the one combination that lies to the user: Magnetic chosen, Auto off,
    // and a manual variation of zero, which is what you get by switching to °M and
    // never opening the variation control. That prints true bearings under a °M
    // label. Somebody who genuinely means zero variation can say so by entering it,
    // which lands here as a non-zero East/West choice or an explicit later Auto off.
    if (mode === 'magnetic' && !auto && variationDeg === 0) auto = true;
    return { mode, variationDeg, variationEast, auto };
  } catch {
    return { ...DEFAULTS };
  }
}

function persist(p: Persisted): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(KEY, JSON.stringify(p));
  } catch {
    /* ignore quota / private-mode / SSR */
  }
}

interface BearingDisplayState extends Persisted {
  /**
   * The last variation the World Magnetic Model gave for the boat's position, or
   * null when Auto is off or there's no fix yet. Live, so it's not persisted; the
   * runner recomputes it. The formatter uses this over the manual value whenever
   * Auto is on and it's non-null.
   */
  autoVariationDeg: number | null;
  /** The variation currently fed to the formatter (auto when live, else manual). */
  effectiveVariationDeg: number;

  setMode: (mode: BearingMode) => void;
  /**
   * Set the manual variation from the menu: an unsigned magnitude in degrees plus
   * whether it's East. Stored signed (East-positive), with the E/W choice kept.
   */
  setManualVariation: (magnitudeDeg: number, east: boolean) => void;
  /** Turn the World Magnetic Model (auto variation) on or off. */
  setAuto: (on: boolean) => void;
  /** The runner feeds the model's figure here (null clears it, e.g. no fix). */
  setAutoComputedVariation: (deg: number | null) => void;
}

/** The variation the formatter should use for a given state. */
function effective(p: {
  auto: boolean;
  autoVariationDeg: number | null;
  variationDeg: number;
}): number {
  return p.auto && p.autoVariationDeg != null ? p.autoVariationDeg : p.variationDeg;
}

const initial = load();
// Bring the formatter in line with the persisted preference at start-up, so the
// first bearing drawn already respects the chosen reference. Auto starts with no
// computed figure, so this is the manual value until the runner fills it in.
const initialEffective = effective({ auto: initial.auto, autoVariationDeg: null, variationDeg: initial.variationDeg });
setBearingDisplay(initial.mode, initialEffective);

export const useBearingDisplayStore = create<BearingDisplayState>((set, get) => {
  // Recompute the effective variation and push it into the formatter, keeping the
  // stored mirror in step so subscribers (e.g. the HUD) re-render on any change.
  const apply = (): void => {
    const s = get();
    const eff = effective(s);
    setBearingDisplay(s.mode, eff);
    set({ effectiveVariationDeg: eff });
  };
  return {
    ...initial,
    autoVariationDeg: null,
    effectiveVariationDeg: initialEffective,
    setMode: (mode) => {
      const s = get();
      // Same repair as load(): switching to Magnetic with Auto off and nothing
      // entered would print true bearings under a °M label, so turn Auto on and let
      // the model supply the variation. Anyone who wants a hand-entered figure can
      // still type one, which turns Auto off on its own.
      const auto = mode === 'magnetic' && !s.auto && s.variationDeg === 0 ? true : s.auto;
      persist({ mode, variationDeg: s.variationDeg, variationEast: s.variationEast, auto });
      set({ mode, auto });
      apply();
    },
    setManualVariation: (magnitudeDeg, east) => {
      const mag = Number.isFinite(magnitudeDeg) ? Math.abs(magnitudeDeg) : 0;
      const variationDeg = east ? mag : -mag;
      persist({ mode: get().mode, variationDeg, variationEast: east, auto: get().auto });
      set({ variationDeg, variationEast: east });
      apply();
    },
    setAuto: (auto) => {
      const s = get();
      persist({ mode: s.mode, variationDeg: s.variationDeg, variationEast: s.variationEast, auto });
      // Clear any stale computed figure when turning Auto off, so the manual value
      // takes over cleanly and a later turn-on recomputes from scratch.
      set(auto ? { auto } : { auto, autoVariationDeg: null });
      apply();
    },
    setAutoComputedVariation: (deg) => {
      set({ autoVariationDeg: deg == null || !Number.isFinite(deg) ? null : deg });
      apply();
    },
  };
});
